/**
 * Calcul du poids net et du poids sacs pour les pesées (standard / inversé).
 * Formule: Poids Net = Poids Brut − (Poids Tare + Poids Sacs)
 */

export type PeseeType = string | undefined;

type SacSources = {
  nbSacsSains?: number | string | null;
  nbSacsRecond?: number | string | null;
  nbSacsDeclares?: number | string | null;
  nbSacsAcceptes?: number | string | null;
  poidsAcceptes?: number | string | null;
  poidsDeclares?: number | string | null;
};

const toNum = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const computePoidsNet = (
  poidsBrut: number,
  poidsTare: number,
  poidsSacs: number = 0,
): number => {
  return Number((poidsBrut - (poidsTare + poidsSacs)).toFixed(2));
};

/**
 * Résout le poids des sacs à déduire du net.
 * - Expédition / transfert : sacs ≈ kg (1 kg/sac)
 * - Réception : poids_acceptes, sinon (sains + recond×4)×capacité, sinon déclarés×capacité
 */
export const resolvePoidsSacs = (opts: {
  typePesee?: PeseeType;
  form?: SacSources;
  weighing?: SacSources & {
    nb_sacs_sains?: number | string | null;
    nb_sacs_recond?: number | string | null;
    nb_sacs_declares?: number | string | null;
    nb_sacs_acceptes?: number | string | null;
    poids_acceptes?: number | string | null;
    poids_declares?: number | string | null;
  };
  conditionnementCapacite?: number;
}): number => {
  const form = opts.form || {};
  const weighing = opts.weighing || {};
  const typePesee = opts.typePesee || 'reception';
  const isExpeditionOrTransfert = typePesee === 'expedition' || typePesee === 'transfert';
  const capacite =
    opts.conditionnementCapacite && opts.conditionnementCapacite > 0
      ? opts.conditionnementCapacite
      : 50;

  const sains =
    toNum(form.nbSacsSains) || toNum(weighing.nb_sacs_sains) || toNum(weighing.nbSacsSains);
  const recond =
    toNum(form.nbSacsRecond) || toNum(weighing.nb_sacs_recond) || toNum(weighing.nbSacsRecond);
  const declares =
    toNum(form.nbSacsDeclares) ||
    toNum(weighing.nb_sacs_declares) ||
    toNum(weighing.nbSacsDeclares);
  const acceptesCount =
    toNum(form.nbSacsAcceptes) ||
    toNum(weighing.nb_sacs_acceptes) ||
    toNum(weighing.nbSacsAcceptes);
  const poidsAcceptes =
    toNum(form.poidsAcceptes) ||
    toNum(weighing.poids_acceptes) ||
    toNum(weighing.poidsAcceptes);
  const poidsDeclares =
    toNum(form.poidsDeclares) ||
    toNum(weighing.poids_declares) ||
    toNum(weighing.poidsDeclares);

  if (isExpeditionOrTransfert) {
    if (sains > 0) return sains;
    if (acceptesCount > 0) return acceptesCount;
    if (poidsAcceptes > 0) return poidsAcceptes;
    if (poidsDeclares > 0) return poidsDeclares;
    if (declares > 0) return declares;
    return 0;
  }

  // Réception
  if (poidsAcceptes > 0) return poidsAcceptes;

  const FACTEUR_RECONDITIONNEMENT = 4;
  if (sains > 0 || recond > 0) {
    return (sains + recond * FACTEUR_RECONDITIONNEMENT) * capacite;
  }

  if (poidsDeclares > 0) return poidsDeclares;
  if (declares > 0) return declares * capacite;
  if (acceptesCount > 0) return acceptesCount * capacite;

  return 0;
};

export const resolveNbSacsAcceptes = (opts: {
  typePesee?: PeseeType;
  form?: SacSources;
  weighing?: SacSources & {
    nb_sacs_sains?: number | string | null;
    nb_sacs_recond?: number | string | null;
    nb_sacs_declares?: number | string | null;
    nb_sacs_acceptes?: number | string | null;
  };
}): number => {
  const form = opts.form || {};
  const weighing = opts.weighing || {};
  const typePesee = opts.typePesee || 'reception';
  const isExpeditionOrTransfert = typePesee === 'expedition' || typePesee === 'transfert';

  const sains =
    toNum(form.nbSacsSains) || toNum(weighing.nb_sacs_sains) || toNum(weighing.nbSacsSains);
  const recond =
    toNum(form.nbSacsRecond) || toNum(weighing.nb_sacs_recond) || toNum(weighing.nbSacsRecond);
  const declares =
    toNum(form.nbSacsDeclares) ||
    toNum(weighing.nb_sacs_declares) ||
    toNum(weighing.nbSacsDeclares);
  const existing =
    toNum(form.nbSacsAcceptes) ||
    toNum(weighing.nb_sacs_acceptes) ||
    toNum(weighing.nbSacsAcceptes);

  if (isExpeditionOrTransfert) {
    if (sains > 0) return sains;
    if (existing > 0) return existing;
    return declares;
  }

  if (sains > 0 || recond > 0) return sains + recond;
  if (existing > 0) return existing;
  return declares;
};

/**
 * Assigne brut/tare selon le processus et le poids capturé à l'étape 3.
 * - standard : capturé = tare, brut déjà connu
 * - inverted : capturé = brut, tare déjà connue
 */
export const resolveBrutTareForStep3 = (opts: {
  isInverted: boolean;
  capturedWeight: number;
  knownBrut?: number | null;
  knownTare?: number | null;
}): { poidsBrut: number; poidsTare: number } => {
  const captured = toNum(opts.capturedWeight);
  const knownBrut = toNum(opts.knownBrut);
  const knownTare = toNum(opts.knownTare);

  if (opts.isInverted) {
    return {
      poidsBrut: captured,
      poidsTare: knownTare,
    };
  }

  return {
    poidsBrut: knownBrut,
    poidsTare: captured,
  };
};
